import {
  pipingNdeConfig,
  transformMatrixData as transformNdeMatrixData,
} from '../../config/disciplines/pipingNde';
import {
  pipingTestingConfig,
  STATUS_OPTIONS,
  transformMatrixData as transformTestingMatrixData,
} from '../../config/disciplines/pipingTesting';
import { DISCIPLINES } from '../../data/codingStructureData';

const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const controlChartTransform = (data) => normalizeRows(data).map((row, index) => ({
  ...row,
  id: row.id || index,
  new_extra_number: row.new_extra_number || '',
  description: row.description || '',
  status: row.status || 'pending',
  notes: row.notes || '',
  discipline: row.discipline || '',
}));

export const JOB_PHONE_MODULES = [
  {
    id: 'piping-nde',
    label: 'NDE Tracker',
    accent: '#1565c0',
    listTitle: (row) => row.isoSheetNumber || `NDE record ${row.id}`,
    listSubtitle: (row) => [row.lineNumber, row.welderNumber, row.ndeType].filter(Boolean).join(' · '),
    statusField: 'ndeType',
    searchFields: ['isoSheetNumber', 'lineNumber', 'welderNumber', 'ndeType', 'fluidCode', 'comments'],
    fields: [
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'welderNumber', label: 'Welder #', type: 'text' },
      { key: 'ndeType', label: 'NDE type', type: 'select', options: ['', 'X-Ray', 'LPI', 'MPI', 'UT', 'VT', 'PMI'] },
      { key: 'isoSheetNumber', label: 'ISO sheet #', type: 'text' },
      { key: 'lineNumber', label: 'Line number', type: 'text' },
      { key: 'fluidCode', label: 'Fluid code', type: 'text', readOnly: true },
      { key: 'qtyJoint', label: 'Qty joint', type: 'number' },
      { key: 'qtyNde', label: 'Qty NDE', type: 'number' },
      { key: 'qtyFailed', label: 'Qty failed', type: 'number' },
      { key: 'comments', label: 'Comments', type: 'textarea' },
    ],
    transform: transformNdeMatrixData,
    api: {
      getData: pipingNdeConfig.api.getData,
      saveChanges: pipingNdeConfig.api.saveChanges,
    },
    buildChangePayload: pipingNdeConfig.buildChangePayload,
  },
  {
    id: 'piping-testing',
    label: 'Testing Tracker',
    accent: '#7c3aed',
    listTitle: (row) => row.testPackageNumber || row.isoSheetNumber || `Test record ${row.id}`,
    listSubtitle: (row) => [row.isoSheetNumber, row.lineSystemId, row.testType].filter(Boolean).join(' · '),
    statusField: 'status',
    searchFields: ['isoSheetNumber', 'testPackageNumber', 'lineSystemId', 'fluidCode', 'systemDescription', 'status', 'inspector'],
    fields: [
      { key: 'isoSheetNumber', label: 'ISO sheet #', type: 'text' },
      { key: 'testPackageNumber', label: 'Test package #', type: 'text' },
      { key: 'lineSystemId', label: 'Line / system ID', type: 'text' },
      { key: 'fluidCode', label: 'Fluid code', type: 'text' },
      { key: 'systemDescription', label: 'System description', type: 'textarea' },
      { key: 'designCode', label: 'Design code', type: 'text' },
      { key: 'designPressPsi', label: 'Design pressure (PSI)', type: 'number' },
      { key: 'designTempF', label: 'Design temperature (°F)', type: 'number' },
      { key: 'testPressPsi', label: 'Test pressure (PSI)', type: 'number' },
      { key: 'testType', label: 'Test type', type: 'select', options: ['', 'Hydrostatic', 'Pneumatic', 'Service Leak', 'Sensitive Leak', 'Alternative Leak'] },
      { key: 'testMedium', label: 'Test medium', type: 'select', options: ['', 'Water (Filtered)', 'Dry Nitrogen', 'Clean Compressed Air', 'Service Fluid', 'Helium/Air Mixture'] },
      { key: 'holdTimeMin', label: 'Hold time (min)', type: 'number' },
      { key: 'status', label: 'Status', type: 'chips', options: STATUS_OPTIONS.filter(Boolean) },
      { key: 'testDate', label: 'Test date', type: 'date' },
      { key: 'inspector', label: 'Inspector / welder #', type: 'text' },
      { key: 'signOff', label: 'Sign-off', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    transform: transformTestingMatrixData,
    api: {
      getData: pipingTestingConfig.api.getData,
      saveChanges: pipingTestingConfig.api.saveChanges,
    },
    buildChangePayload: pipingTestingConfig.buildChangePayload,
  },
  {
    id: 'control-chart',
    label: 'Control Chart',
    accent: '#b45309',
    listTitle: (row) => row.new_extra_number || `Change ${row.id}`,
    listSubtitle: (row) => row.description || 'No description yet',
    statusField: 'status',
    searchFields: ['new_extra_number', 'description', 'status', 'notes', 'discipline'],
    fields: [
      { key: 'new_extra_number', label: 'Extra #', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'status', label: 'Status', type: 'chips', options: ['pending', 'submitted', 'approved', 'tracking', 'cancelled', 'rejected'] },
      { key: 'notes', label: 'Notes', type: 'textarea' },
      { key: 'discipline', label: 'Discipline', type: 'select', options: DISCIPLINES.map((discipline) => discipline.name) },
    ],
    transform: controlChartTransform,
    api: {
      getData: '/control-chart/{projectId}',
      save: '/control-chart/{id}',
      create: '/control-chart',
    },
  },
];

export const HOME_PHONE_MODULES = [
  { id: 'time', route: 'time', label: 'Time Tracker', accent: '#1b7f4e' },
  { id: 'coding', route: 'coding', label: 'Coding Structure', accent: '#6f42c1' },
];

export const getJobPhoneModule = (id) =>
  JOB_PHONE_MODULES.find((module) => module.id === id) || null;

export const isJobPhoneModule = (id) => Boolean(getJobPhoneModule(id));

